import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ErrorService } from 'src/app/services/error.service';
import { PhotoboothService } from 'src/app/api/photobooth.service';

type ApprovalState = 'loading' | 'success' | 'error';

@Component({
  selector: 'app-photobooth-approval',
  templateUrl: './photobooth-approval.component.html',
  styleUrl: './photobooth-approval.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PhotoboothApprovalComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();

  state = signal<ApprovalState>('loading');
  errorMessage = signal<string>('');

  constructor(
    private route: ActivatedRoute,
    private photoboothService: PhotoboothService,
    private messageService: MessageService,
    private errorService: ErrorService,
  ) {}

  ngOnInit(): void {
    // token from email link; approve POST uses normal MC session JWT
    const token = this.route.snapshot.queryParamMap.get('token')?.trim() ?? '';

    if (!token) {
      this.state.set('error');
      this.errorMessage.set('Missing approval token.');
      return;
    }

    this.photoboothService
      .approveProfilePhoto(token)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => {
          this.state.set('success');
          this.messageService.add({
            severity: 'success',
            summary: 'Approved',
            detail: 'The profile photo has been approved and saved.',
            life: 8000,
          });
        },
        error: (err: unknown) => {
          this.state.set('error');
          this.errorMessage.set('Approval failed. Please try again or contact IT.');
          this.errorService.error('Could not approve the photo', err, this.messageService);
        },
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}

